import {
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  MenuBook as MenuBookIcon,
  TrackChanges as TrackIcon,
} from '../icons';
import React, { useEffect } from 'react';
import {
  Typography,
  Box,
  Button,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemText,
  LinearProgress,
  Alert
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import CurrencyDisplay from '../currency/CurrencyDisplay';
import { usePublicTheme } from '../../contexts/PublicThemeContext';
import { getPaymentStatusConfig } from '../../config';

const PaymentSuccessCard = ({
  payment,
  order,
  countdown,
  isRedirecting,
  onCancelRedirect,
  onGoToMenu,
  onGoToOrderTracking,
  onPrintReceipt,
  onEmailReceipt
}) => {
  const { primaryColor, updateMerchantTheme } = usePublicTheme();

  // Update merchant theme when order data is available
  useEffect(() => {
    if (order?.merchant?.settings) {
      updateMerchantTheme(order.merchant.settings);
    }
  }, [order?.merchant?.settings, updateMerchantTheme]);
  const getHoverColor = (color, action = 'darken', amount = 15) => {
    // Simple color manipulation - convert hex to RGB, adjust, convert back
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);

    let newR, newG, newB;
    if (action === 'darken') {
      newR = Math.max(0, r - amount);
      newG = Math.max(0, g - amount);
      newB = Math.max(0, b - amount);
    } else {
      newR = Math.min(255, r + amount);
      newG = Math.min(255, g + amount);
      newB = Math.min(255, b + amount);
    }

    return `rgb(${newR}, ${newG}, ${newB})`;
  };
  const navigate = useNavigate();

  const handleTrackOrder = () => {
    if (onGoToOrderTracking) {
      onGoToOrderTracking();
    } else if (order?.order_number) {
      // Navigate back to menu page with order tracking parameters
      const currentUrl = new URL(window.location);
      const merchantParam = currentUrl.searchParams.get('merchant') ? `merchant=${currentUrl.searchParams.get('merchant')}` : '';
      const tableParam = currentUrl.searchParams.get('table') ? `&table=${currentUrl.searchParams.get('table')}` : '';
      navigate(`/menu?${merchantParam}${tableParam}&track_order=${order.order_number}`);
    }
  };

  const handleBackToMenu = () => {
    if (onGoToMenu) {
      onGoToMenu();
    } else {
      navigate('/menu');
    }
  };

  return (
    <Box sx={{ maxWidth: { xs: '100%', sm: 500 }, mx: 'auto', p: { xs: 1.5, sm: 2 } }}>
      {/* Success Header */}
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <CheckCircleIcon
          sx={{
            fontSize: { xs: 60, sm: 80 },
            color: '#00B14F',
            mb: 2
          }}
        />
        <Typography variant="h5" gutterBottom sx={{ color: '#00B14F', fontWeight: 600, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          Payment Successful!
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Your order has been confirmed and is being prepared.
        </Typography>
      </Box>

      {/* Payment Details */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
          Payment Details
        </Typography>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Transaction ID
            </Typography>
            <Typography variant="body2" noWrap>
              {payment?.transaction_id || payment?.id || 'N/A'}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Status
            </Typography>
            <Box>
              <Chip
                label={getPaymentStatusConfig(payment?.status).label}
                size="small"
                sx={{
                  bgcolor: getPaymentStatusConfig(payment?.status).color,
                  color: 'white',
                  fontSize: '0.75rem'
                }}
              />
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Order Details */}
      {order && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
            Order Details
          </Typography>

          {/* Order Number */}
          <Box sx={{
            mb: 3,
            p: 2,
            textAlign: 'center'
          }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
              Order Number
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: '#000000',
                fontFamily: 'monospace',
                letterSpacing: 1,
                mt: 0.5
              }}
            >
              {order.order_number || 'N/A'}
            </Typography>
          </Box>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">
                Table
              </Typography>
              <Typography variant="body2" fontWeight="medium">
                {order.table_number || order.table?.name || 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">
                Total Amount
              </Typography>
              <CurrencyDisplay
                amount={payment?.amount || order.total || 0}
                variant="body2"
                fontWeight="medium"
              />
            </Grid>
          </Grid>

          {/* Order Items */}
          {order.items && order.items.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                Items ({order.items.length})
              </Typography>
              <List dense sx={{ bgcolor: 'grey.50', borderRadius: 1 }}>
                {order.items.map((item, index) => (
                  <ListItem key={index} divider={index < order.items.length - 1}>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2">
                            {item.quantity}x {item.name}
                          </Typography>
                          <CurrencyDisplay
                            amount={parseFloat(item.total)}
                            variant="body2"
                          />
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </Box>
      )}

      {/* Countdown Alert */}
      {isRedirecting && countdown > 0 && (
        <Alert
          severity="info"
          sx={{ mb: 3 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={onCancelRedirect}
              startIcon={<CancelIcon />}
            >
              Cancel
            </Button>
          }
        >
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Redirecting to order tracking in {countdown} seconds...
            </Typography>
            <LinearProgress
              variant="determinate"
              value={((10 - countdown) / 10) * 100}
              sx={{ height: 6, borderRadius: 3 }}
            />
          </Box>
        </Alert>
      )}

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 3 }}>
        <Button
          variant="contained"
          startIcon={<TrackIcon />}
          onClick={handleTrackOrder}
          disabled={!order?.order_number}
          fullWidth
          sx={{
            bgcolor: primaryColor,
            '&:hover': {
              bgcolor: getHoverColor(primaryColor, 'darken', 15)
            },
            fontWeight: 600
          }}
        >
          TRACK ORDER
        </Button>
        <Button
          variant="outlined"
          startIcon={<MenuBookIcon />}
          onClick={handleBackToMenu}
          fullWidth
        >
          CONTINUE ORDERING
        </Button>
      </Box>

      {/* Footer Note */}
      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          You can track your order by copying the order number or click the Track Order button
        </Typography>
      </Box>
    </Box>
  );
};

export default PaymentSuccessCard;
